import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FlowArrow, Plus, Trash, PencilSimple, DotsSixVertical, Eye, EyeSlash } from "@phosphor-icons/react";
const inputCls = "bg-zinc-950 border-zinc-800 rounded-sm text-zinc-50 focus:border-emerald-600 focus-visible:ring-emerald-600";
const label = "text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5 block";
const STAGE_TYPES = [
  { v: "normal", l: "Normal Task" },
  { v: "customer_approval", l: "Customer Approval (with reminders)" },
  { v: "material_check", l: "Material Receipt Check" },
];

function blankStage() {
  return { key: `stage_${Date.now()}`, name: "", department: "", category: "", type: "normal", expected_hours: 8, offset_days: 2, reminder_days: 1, default_assignee: null, parallel: false, substages: [] };
}

export default function WorkflowTemplates() {
  const [templates, setTemplates] = useState([]);
  const [depts, setDepts] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ name: "", description: "", stages: [], required_docs: null });

  const load = () => api.get("/workflow-templates").then((r) => setTemplates(r.data));
  useEffect(() => {
    load();
    api.get("/departments").then((r) => setDepts(r.data));
    api.get("/document_types").then((r) => setDocTypes(r.data)).catch(() => {});
    api.get("/users?active=true").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", description: "", stages: [blankStage()], required_docs: null }); setOpen(true); };
  const openEdit = (t) => { setEditing(t.id); setForm({ name: t.name, description: t.description || "", stages: t.stages.map((s) => ({ ...s })), required_docs: t.required_docs ?? null }); setOpen(true); };
  const toggleDoc = (n) => setForm((p) => {
    const cur = p.required_docs || [];
    return { ...p, required_docs: cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n] };
  });

  const setStage = (i, k, v) => setForm((p) => ({ ...p, stages: p.stages.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const addStage = () => setForm((p) => ({ ...p, stages: [...p.stages, blankStage()] }));
  const removeStage = (i) => setForm((p) => ({ ...p, stages: p.stages.filter((_, idx) => idx !== i) }));
  const move = (i, dir) => setForm((p) => {
    const arr = [...p.stages]; const j = i + dir;
    if (j < 0 || j >= arr.length) return p;
    [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...p, stages: arr };
  });
  const addSub = (i) => setForm((p) => ({ ...p, stages: p.stages.map((s, idx) => idx === i ? { ...s, substages: [...(s.substages || []), { key: `sub_${Date.now()}`, name: "", department: "" }] } : s) }));
  const setSub = (i, j, k, v) => setForm((p) => ({ ...p, stages: p.stages.map((s, idx) => idx === i ? { ...s, substages: (s.substages || []).map((ss, jj) => jj === j ? { ...ss, [k]: v } : ss) } : s) }));
  const removeSub = (i, j) => setForm((p) => ({ ...p, stages: p.stages.map((s, idx) => idx === i ? { ...s, substages: (s.substages || []).filter((_, jj) => jj !== j) } : s) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Workflow name required");
    if (form.stages.some((s) => !s.name.trim())) return toast.error("Every stage needs a name");
    const payload = { ...form, stages: form.stages.map((s) => ({ ...s, expected_hours: Number(s.expected_hours) || null, offset_days: Number(s.offset_days) || 2, reminder_days: Number(s.reminder_days) || 1 })) };
    try {
      if (editing) await api.put(`/workflow-templates/${editing}`, payload);
      else await api.post("/workflow-templates", payload);
      toast.success("Workflow saved"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this workflow? This cannot be undone.")) return;
    await api.delete(`/workflow-templates/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] animate-fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-1">Automation</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-zinc-50 flex items-center gap-2"><FlowArrow size={26} className="text-emerald-600" weight="bold" /> Workflow Templates</h1>
        </div>
        <div className="flex gap-2 items-center">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search workflows…" className={`${inputCls} w-56`} data-testid="workflow-search" />
          <Button onClick={openNew} data-testid="new-template-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold gap-1.5"><Plus weight="bold" size={16} /> New Workflow</Button>
        </div>
      </div>

      <div className="space-y-3">
        {templates.filter((t) => !search
          || t.name.toLowerCase().includes(search.toLowerCase())
          || (t.description || "").toLowerCase().includes(search.toLowerCase())
          || t.stages.some((s) => (s.name || "").toLowerCase().includes(search.toLowerCase()))).map((t) => (
          <div key={t.id} className="border border-zinc-800 bg-zinc-900/30 p-4" data-testid={`template-${t.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm text-zinc-50 font-semibold">{t.name}</div>
                <div className="text-xs text-zinc-400">{t.description}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(t)} className="border-zinc-800 bg-zinc-950 rounded-sm text-xs gap-1" data-testid={`edit-${t.id}`}><PencilSimple size={14} /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => del(t.id)} data-testid={`delete-${t.id}`} className="border-red-500/30 text-red-400 bg-red-500/5 rounded-sm text-xs gap-1"><Trash size={14} /></Button>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {t.stages.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2">
                  <span className="text-xs bg-zinc-950 border border-zinc-800 px-2 py-1 rounded-sm text-zinc-200">
                    <span className="text-emerald-600 font-mono mr-1">{i + 1}</span>{s.name}
                    {s.type !== "normal" && <span className="text-zinc-500 ml-1">({s.type === "customer_approval" ? "approval" : "material"})</span>}
                  </span>
                  {i < t.stages.length - 1 && <span className="text-zinc-600">→</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-3xl">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "New"} Workflow</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[68vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Workflow Name *</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="tpl-name" /></div>
              <div><label className={label}>Description</label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="border border-zinc-800 p-3">
              <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer">
                <Checkbox checked={form.required_docs !== null} onCheckedChange={(v) => setForm({ ...form, required_docs: v ? [] : null })} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="tpl-custom-docs" />
                Custom required documents for this workflow
              </label>
              <p className="text-[11px] text-zinc-500 mt-1 pl-6">Off = use the global required list from Masters → Document Types. On = orders from this workflow demand only the documents ticked below before any stage can advance.</p>
              {form.required_docs !== null && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-2 pl-6">
                  {docTypes.map((d) => (
                    <label key={d.id} className="flex items-center gap-1.5 text-xs text-zinc-300 cursor-pointer">
                      <Checkbox checked={(form.required_docs || []).includes(d.name)} onCheckedChange={() => toggleDoc(d.name)} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid={`tpl-doc-${d.id}`} />
                      {d.name}
                    </label>
                  ))}
                  {docTypes.length === 0 && <span className="text-xs text-zinc-500">Add document types in Masters → Document Types.</span>}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <label className={label}>Stages (run in order)</label>
              <Button size="sm" onClick={addStage} className="bg-zinc-900 border border-zinc-800 hover:border-emerald-600 text-zinc-200 rounded-sm text-xs gap-1" data-testid="add-stage"><Plus size={14} /> Add Stage</Button>
            </div>
            {form.stages.map((s, i) => (
              <div key={s.key} className="border border-zinc-800 p-3 space-y-2" data-testid={`stage-row-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button onClick={() => move(i, -1)} className="text-zinc-500 hover:text-emerald-400 text-xs leading-none">▲</button>
                    <button onClick={() => move(i, 1)} className="text-zinc-500 hover:text-emerald-400 text-xs leading-none">▼</button>
                  </div>
                  <span className="text-emerald-600 font-mono text-sm w-5">{i + 1}</span>
                  <Input value={s.name} onChange={(e) => setStage(i, "name", e.target.value)} placeholder="Stage name" className={`${inputCls} h-8 flex-1`} />
                  <button onClick={() => removeStage(i)} className="text-zinc-600 hover:text-red-400"><Trash size={15} /></button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pl-9">
                  <Select value={s.department || ""} onValueChange={(v) => setStage(i, "department", v)}>
                    <SelectTrigger className={`${inputCls} h-8 text-xs`}><SelectValue placeholder="Department" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">{depts.map((d) => <SelectItem key={d.id} value={d.name} className="text-xs">{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <div>
                    <Select value={s.type} onValueChange={(v) => setStage(i, "type", v)}>
                      <SelectTrigger className={`${inputCls} h-8 text-xs`} data-testid={`stage-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">{STAGE_TYPES.map((t) => <SelectItem key={t.v} value={t.v} className="text-xs">{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                    {s.type === "customer_approval" && (
                      <p className="text-[10px] text-amber-400 mt-1 leading-snug" data-testid={`stage-type-hint-${i}`}>
                        Only ONE stage in this workflow should be "Customer Approval" — this is the real drawing sign-off step (adds reminder/revision actions and drives the "Drawing Approved" badge). Use "Normal Task" for uploads/other steps.
                      </p>
                    )}
                  </div>
                  <Input value={s.expected_hours} onChange={(e) => setStage(i, "expected_hours", e.target.value)} type="number" placeholder="Hrs" className={`${inputCls} h-8 text-xs`} />
                  <Input value={s.offset_days} onChange={(e) => setStage(i, "offset_days", e.target.value)} type="number" placeholder="Due (days)" className={`${inputCls} h-8 text-xs`} />
                </div>
                <div className="pl-9">
                  <Select value={s.default_assignee || "none"} onValueChange={(v) => setStage(i, "default_assignee", v === "none" ? null : v)}>
                    <SelectTrigger className={`${inputCls} h-8 text-xs`} data-testid={`stage-default-assignee-${i}`}>
                      <SelectValue placeholder="Fixed Responsible Person (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
                      <SelectItem value="none" className="text-xs">Varies per order — ask every time</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name} · {u.department || u.role}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-zinc-500 mt-1">
                    Set this if the SAME person always handles this task (e.g. Accounts entry) — it'll auto-assign on every new order
                    and won't be asked about at creation. Leave as "Varies per order" if it depends on who's free/on the job.
                  </p>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer pl-9 pt-0.5">
                  <input type="checkbox" checked={!!s.parallel} onChange={(e) => setStage(i, "parallel", e.target.checked)} className="accent-emerald-600" data-testid={`stage-parallel-${i}`} />
                  <span><span className="text-emerald-400 font-semibold">Parallel</span> — fires together with adjacent parallel stages once the previous stage completes</span>
                </label>
                {!s.default_assignee && (
                  <button type="button" onClick={() => setStage(i, "ask_at_creation", s.ask_at_creation === false)}
                    data-testid={`stage-ask-at-creation-${i}`}
                    className={`flex items-center gap-1.5 text-xs pl-9 pt-1 font-semibold transition-colors ${s.ask_at_creation !== false ? "text-emerald-400 hover:text-emerald-300" : "text-zinc-500 hover:text-zinc-300"}`}
                    title="Click to toggle whether this stage's 'who's responsible' picker shows up on the New Order form">
                    {s.ask_at_creation !== false
                      ? <><Eye size={14} weight="bold" /> Shown on New Order form — click to hide</>
                      : <><EyeSlash size={14} weight="bold" /> Hidden from New Order form — click to show</>}
                  </button>
                )}
                <div className="pl-9 pt-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">Sub-departments (auto-created as sub-tasks)</span>
                    <button onClick={() => addSub(i)} data-testid={`add-sub-${i}`} className="text-xs text-emerald-400 hover:underline">+ Add sub-department</button>
                  </div>
                  {(s.substages || []).map((ss, j) => (
                    <div key={ss.key || j} className="flex items-center gap-2 mt-1.5">
                      <Input value={ss.name} onChange={(e) => setSub(i, j, "name", e.target.value)} placeholder="Sub-task name (e.g. Busbar Work)" className={`${inputCls} h-7 text-xs flex-1`} />
                      <Select value={ss.department || ""} onValueChange={(v) => setSub(i, j, "department", v)}>
                        <SelectTrigger className={`${inputCls} h-7 text-xs w-36`}><SelectValue placeholder="Dept" /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">{depts.map((d) => <SelectItem key={d.id} value={d.name} className="text-xs">{d.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <button onClick={() => removeSub(i, j)} className="text-zinc-600 hover:text-red-400"><Trash size={13} /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="tpl-save">Save Workflow</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
